import { Injectable, computed, inject } from '@angular/core';
import { BaseRestrictions, CurrencyFormattings, CurrencyRestrictions } from '@models/module/fields/enums';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { ProgressPaymentTabRow } from '@module-instance/dynamic-module/document-details/common/models/progress-payment-tab.models';
import { TranslateService } from '@ngx-translate/core';
import {
  DataListCommandFilterType,
  DataListFilterType,
  DisplayFormats,
  FiltersToApply,
  LocalFilterService,
  TableColumn,
} from '@shared/components/data-list/common';
import { UserStore } from '@signal-store/index';

@Injectable({
  providedIn: 'root',
})
export class ProgressPaymentTabService {
  private translate = inject(TranslateService);
  private userStore = inject(UserStore);
  private localFilterService = inject(LocalFilterService);
  private ppName = computed(
    () =>
      this.userStore.menuItemsList()?.find(item => item.moduleId === SystemModulesEnum.ProgressPayment)?.name ||
      'Progress Payment',
  );

  getTableColumns(): TableColumn[] {
    return [
      {
        id: 0,
        name: 'progress_payment_number',
        displayName: this.translate.instant('pp.ppNumber', { ppName: this.ppName() }),
        sortable: true,
      },
      {
        id: 1,
        name: 'subject',
        displayName: 'pp.subject',
        sortable: true,
      },
      {
        id: 2,
        name: 'progress_payment_period',
        displayName: this.translate.instant('pp.ppPeriod', { ppName: this.ppName() }),
        sortable: true,
      },
      {
        id: 3,
        name: 'document_submitted',
        displayName: 'pp.documentSubmitted',
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.Date,
      },
      {
        id: 4,
        name: 'document_total',
        displayName: 'pp.documentTotal',
        sortable: true,
        displayFormatId: DisplayFormats.Number,
        restrictions: CurrencyRestrictions,
        formattings: CurrencyFormattings,
      },
      {
        id: 5,
        name: 'document_payment',
        displayName: 'pp.documentPayment',
        sortable: true,
        displayFormatId: DisplayFormats.Number,
        restrictions: CurrencyRestrictions,
        formattings: CurrencyFormattings,
      },
      {
        id: 6,
        name: 'state_name',
        displayName: 'general.table.state',
        sortable: true,
        displayFormatId: DisplayFormats.Status,
      },
    ];
  }

  getFilters(): DataListCommandFilterType[] {
    const getType = (displayFormat: DisplayFormats): DataListFilterType => {
      switch (displayFormat) {
        case DisplayFormats.Date:
          return 'date';
        case DisplayFormats.Number:
          return 'number';
        default:
          return 'text';
      }
    };
    return this.getTableColumns().map<DataListCommandFilterType>(column => ({
      fieldId: column.name,
      name: column.displayName,
      type: getType(column.displayFormatId),
    }));
  }

  filterTableRows(rows: ProgressPaymentTabRow[], filters: FiltersToApply): ProgressPaymentTabRow[] {
    if (!rows) return null;
    if (!rows.length) return [];
    const hasFilters = filters?.filters?.filterList?.length > 0;
    const filtersApplied = hasFilters ? this.localFilterService.getFilteredList(rows, filters) : rows;
    const criteria = filters.criteria || '';
    return !criteria
      ? filtersApplied
      : filtersApplied.filter(row =>
          Object.values(row).some(value => value.toString().toLowerCase().includes(criteria.toLowerCase())),
        );
  }
}
